/* ============================================================
 * ADG Category Tiles — fills collection cards with real artwork.
 *
 * WHERE THE IMAGES COME FROM, in priority order:
 *   1. "Featured designs" picked on the block (data-picks, merchant's
 *      order) — never filtered, they asked for them
 *   2. auto-fill from the artwork index, minus "Never use these"
 *      (data-blocked) and minus the global "Skip designs whose title
 *      contains" (data-skip-titles). That global lever is what kills the
 *      multi-image collage posters across ALL cards at once, so nobody
 *      has to hand-curate 36 collections.
 *
 * HOW MANY THUMBNAILS — driven by how wide the card ACTUALLY ended up,
 * which adg-cat-layout.js measures and writes to data-span:
 *   span 1   ->  2x2 quad                (the normal card)
 *   span 2+  ->  2 large, side by side   (a Featured card, or a lone card
 *                                         that grew to fill its row)
 * A wide card showing the same four thumbnails wouldn't make anything
 * bigger — the win is FEWER, LARGER ones, so each is roughly double.
 *
 * THIN COLLECTIONS — a quad with an empty corner looks broken, so:
 *   1 design    -> one still image
 *   2-3 designs -> one image, crossfading through them
 *   4+          -> the quad, extras cycling
 *
 * NO FLICKER: each slot holds two stacked layers and crossfades, so it is
 * never blank mid-swap. Only on-screen cards animate, and the stagger
 * wraps every 7 cards so any handful you're looking at is evenly offset.
 * ============================================================ */
(function () {
  'use strict';

  /* Prefer the shared, memoised loader when the page has it; otherwise do
     our own fetch. Returns the same {meta, rows} either way, and handles a
     v2 file (flat `artworks`) as well as v3, so an older index still
     renders. Never assume a sibling script is present. */
  function adgLoadIndex(url) {
    if (window.ADGIndex && window.ADGIndex.load) return window.ADGIndex.load(url);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (j) {
        // v3 interns artist/collection/orientation as indices; only the
        // shared loader knows how to expand them. Reading them raw would
        // render nonsense, so refuse — the caller's catch keeps the
        // server-rendered image instead.
        if ((j.meta && j.meta.v || 0) >= 3) {
          throw new Error('v3 index needs adg-index.js on this page');
        }
        return { meta: j.meta || {}, rows: j.artworks || [] };
      });
  }

  /* ---- pane images: the right rung, in the right format ----------------
     Panes paint as CSS backgrounds, so <picture> cannot negotiate format
     here the way it does on a card — we pick the URL ourselves.

     Measured on this catalogue: a half pane renders 134-190px and a wide
     pane 272-384px, and every one of them was loading the 512 JPEG at
     46.7 KB. One Shop-by-Collection page pulled 132 of them, ~6.2 MB.
     The 256 WebP is 11.7 KB.

     Only rows that came from the index carry `cdn`; product photos are
     Shopify CDN URLs and are left exactly as they are. */
  var RUNGS = [256, 512, 800, 1024];
  var WEBP = (function () {
    try {
      return document.createElement('canvas')
        .toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } catch (e) { return false; }
  })();

  function rungFor(cssPx) {
    var need = (cssPx || 0) * (window.devicePixelRatio || 1);
    for (var i = 0; i < RUNGS.length; i++) if (RUNGS[i] >= need) return RUNGS[i];
    return RUNGS[RUNGS.length - 1];
  }

  function srcFor(item, cssPx) {
    if (!item || !item.cdn) return item ? item.img : '';
    return item.cdn + '/' + rungFor(cssPx) + '.' + (WEBP ? 'webp' : 'jpg');
  }

  var FADE = { off: 0, fade: 900, dissolve: 2000, swap: 120 };
  var registry = [];
  var spanBound = false;

  /* THE THEME EDITOR IS A DIFFERENT WORLD (Justin, 08-09: "it doesn't feel
     wired ... I suspect it isn't even hooked up"). Changing a setting makes
     Shopify swap in freshly rendered HTML and fire shopify:section:load —
     it does NOT re-run page scripts. This file used to run exactly once, so
     after any edit the new markup sat there with its skeleton still up and
     nothing ever filled it. Every setting looked dead. Two consequences
     below: we re-boot on that event, and we do not shuffle while editing,
     because artwork that reshuffles on every render makes it impossible to
     see what your change did. */
  function inEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* Re-rendered sections leave their old cards detached from the document.
     Their entries must go, or a second build stacks a second set of timers
     on top and the tile flickers twice as fast with every edit. */
  function purgeDetached() {
    for (var i = registry.length - 1; i >= 0; i--) {
      var c = registry[i].card;
      if (!c || (c.isConnected === false)) registry.splice(i, 1);
    }
  }

  function list(el, key) {
    var v = el.dataset[key];
    return v ? v.split('|').filter(Boolean) : [];
  }

  /* PER-PANE CHOICES. data-panes is `source~url` per pane, pipe-joined, in
     the order the merchant sees them: Pane 1 top-left, 2 top-right, 3
     bottom-left, 4 bottom-right. "auto" (or a pane whose item has no image)
     means leave that slot to the auto-fill, so a half-configured card still
     comes out full. Absent attribute = every pane automatic = the exact
     behaviour that shipped before any of this. */
  function parsePanes(raw) {
    var out = [null, null, null, null];
    if (!raw) return out;
    raw.split('|').forEach(function (part, i) {
      if (i > 3) return;
      var cut = part.indexOf('~');
      var kind = cut === -1 ? part : part.slice(0, cut);
      var url = cut === -1 ? '' : part.slice(cut + 1);
      var wide = false;
      if (kind.indexOf('+wide') !== -1) { wide = true; kind = kind.replace('+wide', ''); }
      if (kind === 'auto' && !wide) return;         // nothing to say
      out[i] = { img: url || null, o: null, kind: kind, wide: wide, forced: !!url };
    });
    return out;
  }

  /* The quad is built as [bottom-left, bottom-right, top-left, top-right],
     so a merchant counting panes left-to-right, top-to-bottom means slots
     2, 3, 0, 1. Getting this backwards would put "Pane 1" in the bottom
     corner, which is exactly the kind of thing that made the old panel feel
     arbitrary. */
  var PANE_AT_SLOT = [2, 3, 0, 1];

  function skipMatcher(raw) {
    var terms = (raw || '').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
    if (!terms.length) return function () { return false; };
    return function (title) {
      var t = (title || '').toLowerCase();
      for (var i = 0; i < terms.length; i++) if (t.indexOf(terms[i]) !== -1) return true;
      return false;
    };
  }

  /* TWO pools, not one.
     A product mockup is a photograph of a room that happens to contain art,
     so at tile size it reads as a smudge; flat artwork is the piece at full
     bleed and survives any size. Mixing them keeps the wall of colour AND
     says "this is a canvas" — and it means one bad photograph costs a corner
     of a tile instead of the whole tile.

     They were previously concatenated into a single list with picks first,
     which is why Shop by Product came out wall-to-wall product with no
     artwork left in it. */
  function shuffled(a) {
    var out = a.slice();
    for (var i = out.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = out[i]; out[i] = out[j]; out[j] = t;
    }
    return out;
  }

  function poolsFor(card, indexArt, skip, landscapeMode, artOrder) {
    var blocked = list(card, 'blocked');
    var seen = {};

    // Hand-picked designs first, in the merchant's order, never filtered nor
    // shuffled — they asked for them, in that order. Then the artwork index
    // for this collection, which in index order means every tile leads with
    // the same four designs on every visit; shuffling is the default because
    // that order is an arbitrary sequence, not a ranking.
    var art = list(card, 'picks').map(function (url) { return { img: url, o: null, pinned: true }; });
    art.forEach(function (p) { seen[p.img] = 1; });
    var rows = indexArt || [];
    if (artOrder !== 'collection' && !inEditor()) rows = shuffled(rows);
    rows.forEach(function (a) {
      if (blocked.indexOf(a.h) !== -1) return;
      if (skip(a.t)) return;
      if (landscapeMode !== 'wide' && a.o === 'landscape') return;
      if (seen[a.img]) return;
      art.push({ img: a.img, cdn: a.cdn, o: a.o });
      seen[a.img] = 1;
    });
    // Only if neither produced anything: the flat art the section read off
    // the products themselves. That is what gives a product-format collection
    // any artwork at all — the index is keyed by DESIGN collection, so it has
    // no rows for handles like `canvas-prints`.
    // PARALLEL means the two pools are the same products in the same order —
    // the flat art and the photograph of product N sit at index N in each. A
    // pane taking art[k] and another taking product[k] would then show the
    // same design twice in one tile, which is exactly what Shop by Product
    // did: Uinta and Woody as both the artwork panes and the product panes.
    // render() indexes around it rather than the pools being rebuilt.
    var parallel = false;
    if (!art.length) {
      var supplied = 0;
      list(card, 'art').forEach(function (entry) {
        supplied++;
        // `shape~url`. Landscape obeys the same setting the index art obeys:
        // dropped on Skip, given the wide top slot on Wide. Without the shape
        // a landscape piece letterboxed to 57% of a 3:4 pane, which is the
        // white band that made Shop by Product look gappy.
        var i = entry.indexOf('~');
        var shape = i > 0 ? entry.slice(0, i) : 'p';
        var url = i > 0 ? entry.slice(i + 1) : entry;
        if (landscapeMode !== 'wide' && shape === 'l') return;
        if (seen[url]) return;
        art.push({ img: url, o: shape === 'l' ? 'landscape' : null });
        seen[url] = 1;
      });
      // The two lists are only index-aligned while nothing was dropped. Once
      // a landscape is filtered out they are not, and pane k must go back to
      // indexing each pool from its own start.
      parallel = art.length > 0 && art.length === supplied;
    }

    // Each product entry arrives as `kind~url`. A room shot crops to the pane
    // (it has background to spare, and cropping also removes the letterbox a
    // square 1200x1200 photo leaves in a 3:4 box). The product-on-its-own
    // shot does NOT: it is framed tight, so a crop cuts the edges off the
    // canvas and it stops reading as an object — and it needs none, because
    // its white ground and the tile's white ground are the same colour.
    var product = list(card, 'product').map(function (entry) {
      var i = entry.indexOf('~');
      var kind = i > 0 ? entry.slice(0, i) : 'r';
      var url = i > 0 ? entry.slice(i + 1) : entry;
      return { img: url, o: null, product: true, crop: kind !== 's' };
    });
    return { art: art, product: product, parallel: parallel };
  }

  /* How many panes show a product photograph and how many show artwork.
     The merchant asks for N; this is where reality trims it — a collection
     with two artworks and six product shots cannot honour "one product pane"
     without leaving holes. Returns null when the tile cannot be filled at
     all, and the caller collapses to a single rotating pane. */
  function planPanes(slots, artCount, prodCount, want) {
    var p = Math.min(want, slots, prodCount);
    var a = slots - p;
    if (a > artCount) {                       // short of artwork — product takes the gap
      p = Math.min(slots, p + (a - artCount), prodCount);
      a = slots - p;
    }
    if (a > artCount || p > prodCount) return null;
    return { art: a, product: p };
  }

  /* One pane that alternates. "One image or two and then they change out",
     with the artwork rotating through between the product shots. */
  function interleave(art, prod) {
    if (!prod.length) return art;
    if (!art.length) return prod;
    var out = [], i = 0, j = 0;
    while (i < art.length || j < prod.length) {
      if (i < art.length) out.push(art[i++]);
      if (j < prod.length) out.push(prod[j++]);
    }
    return out;
  }

  /* A slot = two stacked layers we crossfade between. Never blank.
     `is-product` rides on the LAYER, not the slot, because a rotating pane
     can hold a photograph one beat and artwork the next and each needs its
     own sizing — cover for photographs, contain for art. */
  /* Dress one layer for its image. Room shots crop (is-cropped); artwork
     contains untouched. The remaining case is the solo product shot: framed
     tight, so it must not crop — but a square legacy photo then sits small
     in the 3:4 pane on raw white, the "little square ones". The underlay
     setting fills the frame behind exactly those: 'blur' draws a blown-up
     soft copy of the same photo, 'panel' a quiet neutral ground. The sharp
     image moves to a <b> child that inherits the layer's background-image,
     so the rotation JS keeps swapping ONE url and both copies follow. */
  function dress(layer, item, mode) {
    layer.style.backgroundImage =
      'url("' + srcFor(item, layer.clientWidth || layer.offsetWidth) + '")';
    layer.classList.toggle('is-cropped', !!item.crop);
    var lay = mode && mode !== 'none' && !!item.product && !item.crop;
    layer.classList.toggle('is-underlaid', lay);
    layer.classList.toggle('is-underlaid--panel', lay && mode === 'panel');
    if (lay && !layer.firstChild) layer.appendChild(document.createElement('b'));
    else if (!lay && layer.firstChild) layer.removeChild(layer.firstChild);
  }

  function makeSlot(item, cls, mode) {
    var slot = document.createElement('span');
    if (cls) slot.className = cls;
    var a = document.createElement('i'), b = document.createElement('i');
    dress(a, item, mode);
    a.classList.add('on');
    slot.appendChild(a); slot.appendChild(b);
    slot._layers = [a, b];
    slot._front = 0;
    slot._mode = mode || 'none';
    return slot;
  }
  // Decode the incoming image BEFORE crossfading to it. A background-image
  // loads asynchronously, so fading straight away meant the incoming layer
  // faded up EMPTY (white) and the artwork popped in a moment later — the
  // "fades to white then clunks" glitch. Preloading makes every swap a true
  // image-to-image dissolve. Cached images resolve instantly, so nothing is
  // slowed down; a failed load simply skips the swap rather than showing white.
  var preloaded = {};
  function swapSlot(slot, item) {
    // Preload the SAME url dress() will paint, or the decode-before-fade
    // guarantee is lost and the layer fades up empty.
    var probe = slot._layers && slot._layers[0];
    var url = srcFor(item, probe ? (probe.clientWidth || probe.offsetWidth) : 0);
    function apply() {
      var back = slot._layers[1 - slot._front], front = slot._layers[slot._front];
      dress(back, item, slot._mode);
      requestAnimationFrame(function () {
        back.classList.add('on');
        front.classList.remove('on');
        slot._front = 1 - slot._front;
      });
    }
    if (preloaded[url]) { apply(); return; }
    var img = new Image();
    img.onload = function () { preloaded[url] = 1; apply(); };
    img.onerror = function () { /* leave the current image up */ };
    img.src = url;
  }

  // Warm the next few images for a slot so its first swap is instant too.
  function prefetch(items, cssPx) {
    (items || []).slice(0, 3).forEach(function (p) {
      var url = srcFor(p, cssPx);
      if (preloaded[url]) return;
      var i = new Image();
      i.onload = function () { preloaded[url] = 1; };
      i.src = url;
    });
  }

  /* Build (or rebuild) one card's tiles for its CURRENT width. */
  function render(entry) {
    var card = entry.card, artAll = entry.art, prodAll = entry.product;
    var span = parseInt(card.dataset.span, 10) || 1;
    var wanted = parseInt(card.dataset.tileCount, 10) || 4;
    var style = card.dataset.tileStyle || 'auto';
    var mode = card.dataset.underlay || 'none';

    var media = card.querySelector('.adg-cat__media');
    if (!media) return;

    // Read every render: the theme editor re-renders the card with new
    // settings, and a pane the merchant just set must land immediately.
    var paneSpec = parsePanes(card.dataset.panes);
    var forcedFirst = paneSpec[0] || paneSpec[1] || paneSpec[2] || paneSpec[3];

    // AUTO: mosaic on big cards, one rotating image on small ones. The
    // COLUMN COUNT is the size signal — a band of five across gives smaller
    // cards than a band of three. The live engine's chosen count (the grid's
    // data-cols, written by adg-cat-layout) is preferred because it is the
    // real row; Liquid's data-tile-cols is the fallback before the engine
    // has run. The threshold is the merchant's: "Four-pane cards down to"
    // (data-quad-max, default 4) — a quad in a row of four is an
    // eighth-of-a-row image, which Justin ruled out for the product pages
    // (2026-08-05) while the collections pages keep their signed-off
    // 4-across quads by keeping the default.
    //
    // STILL never a measured pixel width. That was tried on 2026-08-04 and
    // flattened the page's rhythm into identical quads. Column COUNT is the
    // design's own unit; pixels are not.
    if (style === 'auto') {
      var grid = card.closest('.adg-cats__grid');
      var liveCols = (grid && parseInt(grid.dataset.cols, 10)) || parseInt(card.dataset.tileCols, 10) || 4;
      var quadMax = parseInt(card.dataset.quadMax, 10) || 4;
      style = liveCols <= quadMax ? 'mosaic' : 'rotate';
    }

    var slots;
    if (style !== 'mosaic' || wanted === 1) slots = 1;
    else if (span >= 2) slots = 2;          // wide card: fewer, bigger
    else slots = wanted;                     // normal card: the quad
    if (artAll.length + prodAll.length < slots) slots = 1;  // thin: one crossfading image

    if (entry.slots === slots) return;       // already right for this width
    entry.slots = slots;

    media.removeAttribute('data-tile-skeleton');   // the real panes take over
    media.innerHTML = '';
    media.classList.toggle('adg-cat__media--mosaic', slots > 1);
    media.classList.toggle('adg-cat__media--rotate', slots === 1);
    media.classList.toggle('is-wide', slots === 2 && span >= 2);

    // ONE card shape, not two. A mosaic is always two rows of two — except the
    // TOP row can merge into a single wide slot to show a landscape, then split
    // back into two portraits. Both states are exactly the same height (the row
    // is 1fr either way), so the morph never moves anything on the page.
    // That is why a collection with a single landscape still works: it gets its
    // turn, then the card returns to a normal quad instead of freezing.
    var land = [], port = [];
    artAll.forEach(function (p) { (p.o === 'landscape' ? land : port).push(p); });

    // The setting is expressed as "N panes out of four", so a card that ended
    // up with fewer panes gets the same PROPORTION rather than the same count.
    var asked = parseInt(card.dataset.productPanes, 10) || 0;
    var want = Math.round(asked * slots / 4);
    var plan = planPanes(slots, port.length, prodAll.length, want);
    if (!plan) { slots = 1; entry.slots = 1; plan = { art: port.length ? 1 : 0, product: port.length ? 0 : 1 }; }

    var cells = [], queues = [];
    if (slots === 4 && (port.length + prodAll.length) >= 4) {
      var top = document.createElement('div');
      top.className = 'adg-cat__row';
      var bottom = document.createElement('div');
      bottom.className = 'adg-cat__row';

      // Product panes fill from the BOTTOM row outward. The top row is the one
      // that merges into the wide landscape slot, so keeping product out of it
      // means the two features can never collide — and the swap guard below,
      // which already protects cell indexes 2 and 3, stays correct.
      // When the pools are parallel, pane k takes index k from whichever pool
      // it is drawing — four different products. Otherwise the artwork is a
      // separate pool entirely and starts at its own beginning. Parallel
      // indexing only works while the art pool is long enough to reach the
      // last pane; a two-image collection would otherwise run off the end and
      // repeat (artovision-shadowboxes is the live case).
      var par = entry.parallel && port.length >= 4;
      var made = [];
      for (var k = 0; k < 4; k++) {
        var isProd = k < plan.product;
        var forced = paneSpec[PANE_AT_SLOT[k]];
        var pick;
        if (forced && forced.img) {
          pick = forced;                                   // a picture they chose
        } else if (forced && forced.kind === 'auto_photo') {
          pick = prodAll[k] || prodAll[0];                 // automatic, but a photo
        } else if (forced && forced.kind === 'auto_art') {
          pick = port[k] || port[0];                       // automatic, but artwork
        } else {
          pick = isProd ? prodAll[k] : port[par ? k : k - plan.product];
        }
        made.push(makeSlot(pick || port[0] || prodAll[0], 'half', mode));
      }
      // top row: a wide slot AND two halves, stacked; we crossfade between them.
      // The wide slot covers the WHOLE top row, so it can only run when both
      // top-row panes are artwork.
      var wideSlot = null;
      // PANE 1 SET TO WIDE. Justin reached for the double-width toggle
      // expecting landscape art to fit and got a wider CARD instead. This is
      // the control he actually wanted, at the level he wanted it: pane 1
      // takes the whole top row, which is the only slot shaped for a
      // landscape piece. Held open — no morphing back to two portraits,
      // because the merchant chose this deliberately.
      var widePane = paneSpec[0] && paneSpec[0].wide ? paneSpec[0] : null;
      if (widePane) {
        wideSlot = makeSlot(widePane.img ? widePane : (land[0] || port[0] || prodAll[0]), 'wide', mode);
        top.appendChild(wideSlot);
        top.classList.add('show-wide');
      } else if (land.length && plan.product <= 2) {
        wideSlot = makeSlot(land[0], 'wide', mode);
        top.appendChild(wideSlot);
      }
      top.appendChild(made[2]); top.appendChild(made[3]);
      bottom.appendChild(made[0]); bottom.appendChild(made[1]);
      media.appendChild(top); media.appendChild(bottom);

      cells = made;                          // [b1, b2, t1, t2] — the plain swappers
      var spareArt = port.slice(par ? 4 : plan.art);
      var spareProd = prodAll.slice(plan.product);
      queues = made.map(function (s, i) {
        // A CHOSEN PICTURE HOLDS; ITS NEIGHBOURS KEEP MOVING.
        // Both halves of that sentence are requirements, learned the hard
        // way in one sitting (Justin, 08-09). Letting a chosen pane rotate
        // means the design you picked is gone nine seconds later — "the
        // product that was chosen doesn't show". Freezing every pane means
        // the wall stops breathing — "the animations no longer have that
        // nice trickle". The mistake was never the pin: it was seeding ALL
        // FOUR panes of every card, which left nothing to rotate. Set the
        // one corner you care about; the other three carry on.
        if (paneSpec[PANE_AT_SLOT[i]] && paneSpec[PANE_AT_SLOT[i]].img) return [];
        // A pane that SAYS "product photo" must keep showing product photos
        // as it rotates, and one that says artwork must keep showing artwork.
        // Without this the queue followed the old whole-card plan and a
        // labelled pane quietly drifted to the other kind a few beats later,
        // which would make the new labels wrong the moment you looked away.
        var ps = paneSpec[PANE_AT_SLOT[i]];
        if (ps && ps.kind === 'auto_photo') return spareProd;
        if (ps && ps.kind === 'auto_art') return spareArt;
        return i < plan.product ? spareProd : spareArt;
      });
      entry.top = top;
      entry.wideSlot = wideSlot;
      // A deliberately-wide pane is held, not cycled: an empty land queue is
      // what stops the morph loop from folding it back into two portraits.
      entry.landQueue = (wideSlot && !widePane) ? land : [];
      entry.landIdx = 0;
      entry.showingWide = !!widePane;
    } else if (slots === 1) {
      // One pane. With both kinds available it alternates between them, so the
      // tile still says "product" without ever being four smudges at once.
      // Parallel pools would alternate art and photo of the SAME design beat
      // after beat, which reads as a stutter; offsetting the photographs by
      // one keeps every swap a different piece.
      var prodSolo = (entry.parallel && prodAll.length > 1)
        ? prodAll.slice(1).concat(prodAll.slice(0, 1))
        : prodAll;
      var mixed = want > 0 ? interleave(port, prodSolo) : (port.length ? port : prodAll);
      // A narrow card shows ONE picture. If the merchant named any pane, the
      // first one they named is the one that picture should be — otherwise
      // setting Pane 1 would appear to do nothing on small cards.
      var solo = makeSlot(forcedFirst || mixed[0], 'solo', mode);
      media.appendChild(solo);
      cells = [solo];
      queues = [mixed.slice(1)];
      entry.top = null; entry.wideSlot = null; entry.landQueue = [];
    } else {
      for (var i = 0; i < slots; i++) {
        var prod = i < plan.product;
        // Two-up (a wide card): panes 1 and 2 in reading order.
        var cell = makeSlot(paneSpec[i] || (prod ? prodAll[i] : port[i - plan.product]), null, mode);
        media.appendChild(cell);
        cells.push(cell);
        queues.push(prod ? prodAll.slice(plan.product) : port.slice(plan.art));
      }
      entry.top = null; entry.wideSlot = null; entry.landQueue = [];
    }
    entry.cells = cells;
    entry.queues = queues;
    entry.turn = 0; entry.qi = 0;
    // Warm what's coming so the first crossfade is as clean as the rest —
    // at the size those panes will actually paint, or we would preload one
    // rung and then request a different one.
    var halfW = cells[0] ? (cells[0].clientWidth || cells[0].offsetWidth) : 0;
    prefetch(queues[0], halfW);
    if (entry.landQueue) {
      var wideW = entry.wideSlot && entry.wideSlot._layers && entry.wideSlot._layers[0]
        ? (entry.wideSlot._layers[0].clientWidth || halfW * 2) : halfW * 2;
      prefetch(entry.landQueue, wideW);
    }
  }

  function build(cards, root) {
    var skip = skipMatcher(root.dataset.skipTitles);
    var anim = root.dataset.anim || 'fade';
    var landscapeMode = root.dataset.landscape || 'skip';
    var artOrder = root.dataset.artOrder || 'shuffle';
    var fadeMs = FADE[anim] != null ? FADE[anim] : FADE.fade;
    root.style.setProperty('--adg-tile-fade', fadeMs + 'ms');

    // Shared with the design grid on the same page — one fetch, not two.
    // Falls back to its own fetch when the shared loader is not on the page:
    // this section is rendered by templates that do not all include it, and
    // assuming it was there took every tile down (08-10).
    adgLoadIndex((root.dataset.indexUrl || '').trim())
      .then(function (json) {
        var byColl = {};
        (json.rows || []).forEach(function (a) {
          (a.c || []).forEach(function (h) {
            if (!byColl[h]) byColl[h] = [];
            byColl[h].push(a);
          });
        });
        // The pool each tile rotates through is the collection's 40 BEST
        // SELLERS (index field `bs`, ranked off Shopify's own best-selling
        // order), not the first 40 alphabetical rows the file happens to
        // start with. The editor has promised "top sellers" all along —
        // this makes it true. Rows without a rank sort last.
        Object.keys(byColl).forEach(function (h) {
          byColl[h].sort(function (x, y) { return (x.bs || 1e9) - (y.bs || 1e9); });
          if (byColl[h].length > 40) byColl[h].length = 40;
        });

        cards.forEach(function (card) {
          if (!card.dataset.collection) return;
          var pools = poolsFor(card, byColl[card.dataset.collection], skip, landscapeMode, artOrder);
          if (!pools.art.length && !pools.product.length) {
            // Nothing to fill the tile with. The server counted images this
            // card's FILTERS then removed (blocked, skip-titles, landscape),
            // so a skeleton may be standing — take it down and let the
            // static image back through.
            stripSkeleton(card);
            return;
          }
          var entry = {
            card: card, art: pools.art, product: pools.product, parallel: pools.parallel,
            slots: null, cells: [], queues: [], turn: 0, qi: 0
          };
          registry.push(entry);
          render(entry);
        });

        // The layout engine re-measures on resize and reports new spans; a card
        // that changed width re-renders with the right number of thumbnails.
        // Bound ONCE for the page — a second build (an editor re-render) used
        // to add a second listener, so every span event rendered twice.
        if (!spanBound) {
          spanBound = true;
          document.addEventListener('adg:span', function () {
            purgeDetached();
            registry.forEach(render);
          });
        }

        if (anim === 'off') return;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        var visible = new WeakSet();
        var io = ('IntersectionObserver' in window)
          ? new IntersectionObserver(function (entries) {
              entries.forEach(function (e) {
                if (e.isIntersecting) visible.add(e.target); else visible.delete(e.target);
              });
            }, { rootMargin: '100px' })
          : null;

        var everyMs = (parseFloat(cards[0] && cards[0].dataset.cycle) || 9) * 1000;
        var stagger = parseInt(cards[0] && cards[0].dataset.cycleStagger, 10);
        if (isNaN(stagger)) stagger = 700;

        registry.forEach(function (entry, idx) {
          if (io) io.observe(entry.card); else visible.add(entry.card);
          // Stagger WRAPS every 7 cards. Multiplying by the raw index spread 34
          // cards across nearly a minute, so whatever you were looking at sat
          // still for ages and then two moved at once.
          setTimeout(function () {
            setInterval(function () {
              // A card from a re-rendered section is detached; its timer
              // outlives it and must do nothing rather than animate a node
              // that is no longer on the page.
              if (entry.card.isConnected === false) return;
              if (!visible.has(entry.card)) return;
              if (!entry.cells.length) return;
              entry.turn++;
              // Every third beat, the top row morphs: two portraits merge into a
              // landscape, or a landscape splits back into two portraits. Load
              // the incoming content BEFORE the crossfade so nothing pops.
              if (entry.top && entry.landQueue.length) {
                if (entry.showingWide) {
                  // it had its moment — split back into two portraits
                  entry.top.classList.remove('show-wide');
                  entry.showingWide = false;
                  return;
                }
                if (entry.turn % 3 === 0) {
                  // merge the top row into one landscape. Load it BEFORE the
                  // crossfade so nothing pops.
                  swapSlot(entry.wideSlot,
                           entry.landQueue[entry.landIdx % entry.landQueue.length]);
                  entry.landIdx++;
                  entry.top.classList.add('show-wide');
                  entry.showingWide = true;
                  return;
                }
              }
              var n = entry.turn % entry.cells.length;
              var q = entry.queues[n];
              if (!q || !q.length) return;
              // don't swap a top-row portrait while the landscape is covering it
              if (entry.showingWide && n >= 2) return;
              swapSlot(entry.cells[n], q[entry.qi % q.length]);
              entry.qi++;
            }, everyMs);
          }, (idx % 7) * stagger);
        });
      })
      .catch(function (err) {
        console.warn('[adg-cat-tiles] index unavailable, keeping server images', err);
        // Every skeleton comes down so the static images the server rendered
        // are the page again — a failed fetch must never strand gray panes.
        cards.forEach(stripSkeleton);
      });
  }

  /* The server renders the tile's final geometry (adg-collage.liquid) so the
     first paint does not rebuild in front of the shopper. When the client
     CANNOT honour it — index unreachable, or filters emptied the pools —
     the skeleton is removed and the hidden static <img> shows again. */
  function stripSkeleton(card) {
    var media = card.querySelector('.adg-cat__media');
    if (!media || !media.hasAttribute('data-tile-skeleton')) return;
    media.removeAttribute('data-tile-skeleton');
    media.classList.remove('adg-cat__media--mosaic', 'adg-cat__media--rotate');
    [].slice.call(media.querySelectorAll('.adg-cat__row, .solo')).forEach(function (n) {
      n.parentNode.removeChild(n);
    });
  }

  function bootAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    // A section re-render hands us the section element itself, which may BE
    // the grid rather than contain it.
    var roots = [].slice.call(host.querySelectorAll('.adg-cats[data-index-url]'));
    if (host.matches && host.matches('.adg-cats[data-index-url]')) roots.push(host);
    roots.forEach(function (root) {
      var cards = [].slice.call(root.querySelectorAll('.adg-cat[data-collection]'));
      if (cards.length) build(cards, root);
    });
  }

  bootAll(document);

  // THE FIX for "changing a setting does nothing": rebuild the tiles Shopify
  // just re-rendered. Purge first so the old cards' entries and timers go.
  document.addEventListener('shopify:section:load', function (e) {
    purgeDetached();
    bootAll(e.target);
  });
  document.addEventListener('shopify:section:unload', purgeDetached);
  // Selecting a block scrolls it into view; nothing to rebuild, but a card
  // that never got its span (hidden when first measured) can catch up.
  document.addEventListener('shopify:block:select', function () {
    purgeDetached();
    registry.forEach(render);
  });
})();
